import React from "react";
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  Stack,
} from "@mui/material";
import {
  labelForAdminCountry,
  formatRevenueByCurrency,
} from "../common/adminMarketCountries";
import { formatBusinessTypeLabel } from "../common/adminEnumLabels";
import AdminSurface from "../common/AdminSurface";
import { adminTableHeadRow, adminTableRowSx } from "../../styles/adminUiTokens";
import {
  aggregateCountriesFromRows,
  type DashboardCountryRow,
  type DashboardRow,
} from "./dashboardAnalyticsUtils";

interface Props {
  rows: DashboardRow[];
  showCountryDetail?: boolean;
}

const tableSx = {
  tableLayout: "fixed" as const,
  width: "100%",
};

const colWidths = {
  label: "32%",
  metric: "17%",
};

const cellSx = {
  py: 1.25,
  px: 2,
  fontSize: "0.875rem",
  overflow: "hidden",
  textOverflow: "ellipsis",
};

function CountCell({ total, active }: { total: number; active?: number }) {
  return (
    <Box sx={{ textAlign: "right" }}>
      <Typography variant="body2" fontWeight={500} component="span">
        {total}
      </Typography>
      {active != null && (
        <Typography
          variant="caption"
          color="text.secondary"
          display="block"
          lineHeight={1.2}
        >
          {active} active
        </Typography>
      )}
    </Box>
  );
}

function BreakdownTable({
  labelHeader,
  items,
}: {
  labelHeader: string;
  items: {
    key: string;
    label: string;
    row: DashboardRow | DashboardCountryRow;
  }[];
}) {
  if (items.length === 0) return null;

  return (
    <AdminSurface>
      <Box sx={{ overflowX: "auto" }}>
        <Table size="small" sx={tableSx}>
          <colgroup>
            <col style={{ width: colWidths.label }} />
            <col style={{ width: colWidths.metric }} />
            <col style={{ width: colWidths.metric }} />
            <col style={{ width: colWidths.metric }} />
            <col style={{ width: colWidths.metric }} />
          </colgroup>
          <TableHead>
            <TableRow sx={adminTableHeadRow}>
              <TableCell>{labelHeader}</TableCell>
              <TableCell align="right">Companies</TableCell>
              <TableCell align="right">Subscriptions</TableCell>
              <TableCell align="right">Revenue</TableCell>
              <TableCell align="right">Payments</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {items.map(({ key, label, row }, index) => (
              <TableRow key={key} hover sx={adminTableRowSx(index)}>
                <TableCell sx={{ ...cellSx, fontWeight: 500 }}>
                  {label}
                </TableCell>
                <TableCell sx={cellSx} align="right">
                  <CountCell
                    total={row.companiesTotal}
                    active={row.companiesActive}
                  />
                </TableCell>
                <TableCell sx={cellSx} align="right">
                  <CountCell
                    total={row.subscriptionsTotal}
                    active={row.subscriptionsActive}
                  />
                </TableCell>
                <TableCell sx={{ ...cellSx, fontWeight: 500 }} align="right">
                  {formatRevenueByCurrency(row.revenueByCurrency)}
                </TableCell>
                <TableCell sx={{ ...cellSx, fontWeight: 500 }} align="right">
                  {row.completedPayments}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>
    </AdminSurface>
  );
}

function MobileMetricGrid({
  row,
}: {
  row: DashboardRow | DashboardCountryRow;
}) {
  const metrics = [
    ["Companies", `${row.companiesTotal} (${row.companiesActive} active)`],
    [
      "Subscriptions",
      `${row.subscriptionsTotal} (${row.subscriptionsActive} active)`,
    ],
    ["Revenue", formatRevenueByCurrency(row.revenueByCurrency)],
    ["Payments", String(row.completedPayments)],
  ] as const;

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: "1fr 1fr",
        gap: 1,
      }}
    >
      {metrics.map(([label, value]) => (
        <Box key={label}>
          <Typography variant="caption" color="text.secondary">
            {label}
          </Typography>
          <Typography variant="body2" fontWeight={500}>
            {value}
          </Typography>
        </Box>
      ))}
    </Box>
  );
}

const AdminDashboardBreakdown: React.FC<Props> = ({
  rows,
  showCountryDetail = true,
}) => {
  if (rows.length === 0) return null;

  const countryRows = showCountryDetail ? aggregateCountriesFromRows(rows) : [];

  const industryItems = rows.map((row) => ({
    key: row.businessType,
    label: formatBusinessTypeLabel(row.businessType),
    row,
  }));

  const countryItems = countryRows.map((row) => ({
    key: row.countryCode,
    label: labelForAdminCountry(row.countryCode),
    row,
  }));

  return (
    <Stack spacing={3}>
      <Box>
        <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1.5 }}>
          By industry
        </Typography>
        <Box sx={{ display: { xs: "none", md: "block" } }}>
          <BreakdownTable labelHeader="Industry" items={industryItems} />
        </Box>
        <Stack spacing={1.5} sx={{ display: { xs: "flex", md: "none" } }}>
          {rows.map((row) => (
            <Paper
              key={row.businessType}
              variant="outlined"
              sx={{ p: 2, borderRadius: 2 }}
            >
              <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5 }}>
                {formatBusinessTypeLabel(row.businessType)}
              </Typography>
              <MobileMetricGrid row={row} />
            </Paper>
          ))}
        </Stack>
      </Box>

      {showCountryDetail && countryItems.length > 0 && (
        <Box>
          <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1.5 }}>
            By country
          </Typography>
          <Box sx={{ display: { xs: "none", md: "block" } }}>
            <BreakdownTable labelHeader="Country" items={countryItems} />
          </Box>
          <Stack spacing={1.5} sx={{ display: { xs: "flex", md: "none" } }}>
            {countryRows.map((row) => (
              <Paper
                key={row.countryCode}
                variant="outlined"
                sx={{ p: 2, borderRadius: 2 }}
              >
                <Typography
                  variant="subtitle2"
                  fontWeight={700}
                  sx={{ mb: 1.5 }}
                >
                  {labelForAdminCountry(row.countryCode)}
                </Typography>
                <MobileMetricGrid row={row} />
              </Paper>
            ))}
          </Stack>
        </Box>
      )}
    </Stack>
  );
};

export default AdminDashboardBreakdown;
